import { useState } from "react";
import { Button, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { INegociacao } from "@/models/consignado/proposal-billings.model";
import {
  adicionarNegociacaoCobranca,
  atualizarNegociacaoCobranca,
  buscarNegociacoesPorCcb,
  deletarNegociacaoCobranca,
} from "@/services/consignado/proposta-cobranca.service";
import { formatCompetenciaToYYYYMM } from "@/utils/consignado/renderCompetencia.utils";
import ConfirmDialog from "../components/ConfirmDialog";
import CollapsibleSection from "./CollapsibleSection";
import NegociacaoFormModal, { NegociacaoFormValues } from "./NegociacaoFormModal";
import NegociacoesTable from "./NegociacoesTable";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";
import { useCobrancaDetalhe } from "@/hooks/useCobrancaDetalhe";

type Props = {
  numeroCcb: string;
  // UUID da cobrança (`cobranca_id` da listagem). null = vínculo sem cobrança:
  // lista, mas não deixa criar.
  cobrancaId: string | null;
};

// Seção "Negociação" do detalhe da CCB — REAL (HIS-024 / TAR-048, backend
// his-adm-cobranca-020 `efab7a1f`). Lista via GET /proposta/cobrancas/:ccb/
// negociacoes (todas as competências, mais recentes primeiro). Nasce colapsada
// e só dispara a query na 1ª abertura.
//
// Escritas (todas REAIS; a query é invalidada depois de cada uma):
//  - editar: PUT /cobrancas/negociacoes/:id (data, status, descrição).
//  - excluir: DELETE /cobrancas/negociacoes/:id, com confirmação.
//  - criar: POST /cobrancas/:id/negociacoes — como na tela legada, com o UUID
//    da cobrança (`cobrancaId`, da listagem) e o `cobranca_competencia_id`
//    real das competências do detalhe (useCobrancaDetalhe, que só dispara
//    quando o operador abre o modal de criação).
const NEGOCIACOES_QUERY_KEY = "negociacoes-cobranca";

type ModalState = { negociacao: INegociacao | null };

export default function NegociacaoSection({ numeroCcb, cobrancaId }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();
  const [aberta, setAberta] = useState(false);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [negociacaoParaExcluir, setNegociacaoParaExcluir] = useState<INegociacao | null>(null);

  const { data: negociacoes, isLoading, isError } = useQuery<INegociacao[]>({
    queryKey: [NEGOCIACOES_QUERY_KEY, numeroCcb],
    queryFn: () => buscarNegociacoesPorCcb(numeroCcb),
    enabled: aberta,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const criando = !!modal && !modal.negociacao;
  const { data: cobranca, isLoading: isLoadingDetalhe } = useCobrancaDetalhe(cobrancaId, criando);
  const competencias = cobranca?.competencias_resumo ?? [];

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: [NEGOCIACOES_QUERY_KEY, numeroCcb] });

  const salvar = useMutation({
    mutationFn: ({ negociacao, values }: ModalState & { values: NegociacaoFormValues }) => {
      // datetime-local é hora local → ISO com fuso, pro backend gravar certo.
      const dataContato = new Date(values.data_contato).toISOString();
      const campos = { data_contato: dataContato, status: values.status, descricao: values.descricao.trim() };

      if (negociacao?.id) return atualizarNegociacaoCobranca(negociacao.id, campos);

      const competencia = competencias.find((c) => c.competencia_id === values.cobranca_competencia_id);
      if (!cobrancaId || !competencia) {
        return Promise.reject(new Error("Cobrança ou competência não carregada."));
      }
      return adicionarNegociacaoCobranca(cobrancaId, {
        ...campos,
        cobranca_competencia_id: competencia.competencia_id,
        competencia: formatCompetenciaToYYYYMM(competencia.competencia),
      });
    },
    onSuccess(_, { negociacao }) {
      toast({
        title: negociacao ? "Negociação atualizada" : "Negociação adicionada",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setModal(null);
      invalidar();
    },
    onError: toastErro("Erro ao salvar negociação"),
  });

  const excluir = useMutation({
    mutationFn: (negociacao: INegociacao) => deletarNegociacaoCobranca(negociacao.id!),
    onSuccess() {
      setNegociacaoParaExcluir(null);
      invalidar();
    },
    onError: toastErro("Erro ao excluir negociação"),
  });

  const conteudo = isLoading ? (
    <Flex justifyContent="center" py={4}>
      <Spinner color="primary" />
    </Flex>
  ) : isError || !negociacoes ? (
    <Text fontSize="sm" color="gray.500">
      Não foi possível carregar as negociações desta CCB.
    </Text>
  ) : (
    <>
      <Flex justifyContent="flex-end" mb={3}>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="secondary"
          size="sm"
          onClick={() => setModal({ negociacao: null })}
          isDisabled={!cobrancaId}
        >
          Adicionar nova negociação
        </Button>
      </Flex>
      <NegociacoesTable
        negociacoes={negociacoes}
        onEditar={(negociacao) => setModal({ negociacao })}
        onExcluir={setNegociacaoParaExcluir}
        excluindoId={excluir.isPending ? excluir.variables?.id : null}
      />
    </>
  );

  return (
    <>
      <CollapsibleSection
        title="Negociação"
        defaultExpanded={false}
        onToggle={(expanded) => expanded && setAberta(true)}
      >
        {aberta && conteudo}
      </CollapsibleSection>

      <ConfirmDialog
        isOpen={!!negociacaoParaExcluir}
        onClose={() => setNegociacaoParaExcluir(null)}
        onConfirm={() => negociacaoParaExcluir && excluir.mutate(negociacaoParaExcluir)}
        titulo="Excluir negociação"
        descricao="A negociação será removida do histórico desta CCB. Essa ação não pode ser desfeita."
        confirmLabel="Excluir"
        isLoading={excluir.isPending}
      />

      {modal && (
        <NegociacaoFormModal
          isOpen
          onClose={() => setModal(null)}
          negociacao={modal.negociacao}
          competencias={competencias}
          competenciasCarregando={isLoadingDetalhe}
          // Erro já vira toast no onError; o catch evita rejeição sem handler.
          onSubmit={(values) => salvar.mutateAsync({ ...modal, values }).catch(() => undefined)}
          isSaving={salvar.isPending}
        />
      )}
    </>
  );
}
